/**
 * Sold Out Hover Modal
 * Shows pop-up on hover of sold-out products
 * Simple version using pre-loaded collection data
 *
 * Two kinds of trigger:
 *   - sold-out product cards (CARD_PROFILES) on collection pages and carousels;
 *     the popup sits beside the card
 *   - the Sold Out button on the product page ([data-sold-out-hover], rendered by
 *     snippets/product-form.liquid and snippets/sticky-product-bar.liquid); the
 *     popup drops below the button, or above it when the button is near the
 *     bottom of the viewport (the sticky bar)
 * Alternatives always come from the in-stock cards already on the page, so on the
 * product page that means the recommendations carousel.
 */

(function() {
  'use strict';

  /**
   * Every card shape the modal understands. Adding a new one is a matter of adding
   * an entry here — nothing below needs to know which shape it is looking at.
   *   sel      how to find the cards
   *   soldOut  class marking a card as sold out
   *   title / image / price  where to read each field from within a card
   */
  const CARD_PROFILES = [
    {
      sel: '[data-product-item]',
      soldOut: 'productitem--soldout',
      title: '.productitem--title a',
      image: '.productitem--image img',
      price: '[data-product-price-row]'
    },
    {
      // Category carousel (sections/category-carousel.liquid)
      sel: '.ccc__card',
      soldOut: 'ccc__card--soldout',
      title: '.ccc__product-title a',
      image: '.ccc__image--primary',
      price: '.ccc__price'
    }
  ];

  const SoldOutHoverModal = {
    _hideTimer: null,

    init() {
      this.setupModal();
      this.attachHoverListeners();
    },

    setupModal() {
      if (document.getElementById('sold-out-hover-modal')) return;

      const modal = document.createElement('div');
      modal.id = 'sold-out-hover-modal';
      modal.className = 'sold-out-hover-modal';
      modal.innerHTML = `
        <div class="sold-out-hover-modal__content">
          <div class="sold-out-hover-modal__header">
            <h3>This item is sold out</h3>
            <p>Similar products available:</p>
          </div>
          <div class="sold-out-hover-modal__list" id="alternatives-list"></div>
        </div>
      `;

      document.body.appendChild(modal);
    },

    attachHoverListeners() {
      const modal = document.getElementById('sold-out-hover-modal');

      // Find all sold-out products and attach hover listeners directly
      document.addEventListener('DOMContentLoaded', () => this.attachSoldOutListeners());

      // Also run immediately in case DOM is already loaded
      this.attachSoldOutListeners();

      // Keep modal visible when hovering over it
      modal?.addEventListener('mouseenter', () => {
        clearTimeout(this._hideTimer);
        modal.classList.add('sold-out-hover-modal--visible');
      });

      modal?.addEventListener('mouseleave', () => {
        this.hideModal();
      });
    },

    attachSoldOutListeners() {
      CARD_PROFILES.forEach(profile => {
        const soldOutProducts = document.querySelectorAll(profile.sel + '.' + profile.soldOut);

        soldOutProducts.forEach(productItem => {
          // Carousels re-render, so guard against binding the same card twice.
          if (productItem.dataset.soldOutBound === '1') return;
          productItem.dataset.soldOutBound = '1';

          productItem.addEventListener('mouseenter', (e) => {
            // Cancel any hide queued by the card we just came from, or moving between
            // two sold-out cards would open the popup and then let the previous card's
            // timer close it ~100ms later — the second card looked like it did nothing.
            clearTimeout(this._hideTimer);
            this.showModal(e, productItem);
          });

          productItem.addEventListener('mouseleave', (e) => {
            const modal = document.getElementById('sold-out-hover-modal');
            // Delay hide to allow click on modal
            clearTimeout(this._hideTimer);
            this._hideTimer = setTimeout(() => {
              if (modal && !modal.matches(':hover')) {
                this.hideModal();
              }
            }, 100);
          });
        });
      });

      // Product page: the Sold Out button itself is the trigger. It is a real
      // button (aria-disabled, not disabled) so it receives mouse events.
      document.querySelectorAll('[data-sold-out-hover]').forEach(button => {
        if (button.dataset.soldOutBound === '1') return;
        button.dataset.soldOutBound = '1';

        const open = (e) => {
          clearTimeout(this._hideTimer);
          this.showModal(e, button, { placement: 'vertical' });
        };
        const close = () => {
          const modal = document.getElementById('sold-out-hover-modal');
          clearTimeout(this._hideTimer);
          this._hideTimer = setTimeout(() => {
            if (modal && !modal.matches(':hover')) {
              this.hideModal();
            }
          }, 100);
        };

        button.addEventListener('mouseenter', open);
        button.addEventListener('focus', open);
        button.addEventListener('mouseleave', close);
        button.addEventListener('blur', close);
      });
    },

    showModal(event, productItem, options = {}) {
      const modal = document.getElementById('sold-out-hover-modal');
      const rect = productItem.getBoundingClientRect();
      const modalWidth = 300;
      const gap = 12;

      if (options.placement === 'vertical') {
        // Render first so the popup has its real height to measure against.
        this.renderAlternatives(this.getAlternatives(productItem));

        const width = modal.offsetWidth || 360;
        const height = modal.offsetHeight || 400;

        // Left-align with the button, but keep the whole popup on screen.
        let left = rect.left;
        if (left + width > window.innerWidth - 10) {
          left = window.innerWidth - width - 10;
        }

        // Below the button by default; above it when there is no room below
        // (the sticky bar sits at the bottom of the viewport).
        let top = rect.bottom + gap;
        if (top + height > window.innerHeight - 10) {
          top = rect.top - height - gap;
        }

        modal.style.left = Math.max(10, left) + 'px';
        modal.style.top = Math.max(10, top) + 'px';
        modal.classList.add('sold-out-hover-modal--visible');
        return;
      }

      // Calculate horizontal position - prefer right side, but switch to left if it goes off-screen
      let left = rect.right + gap;
      if (left + modalWidth > window.innerWidth) {
        left = rect.left - modalWidth - gap;
      }

      // Calculate vertical position - center with product, but keep in viewport
      let top = rect.top + (rect.height / 2) - 80;
      if (top < 0) {
        top = 10;
      }
      if (top + 400 > window.innerHeight) {
        top = window.innerHeight - 410;
      }

      // Apply positioning
      modal.style.left = Math.max(10, left) + 'px';
      modal.style.top = top + 'px';

      // Get alternatives (products from same collection)
      const alternatives = this.getAlternatives(productItem);
      this.renderAlternatives(alternatives);

      // Show modal
      modal.classList.add('sold-out-hover-modal--visible');
    },

    hideModal() {
      const modal = document.getElementById('sold-out-hover-modal');
      modal.classList.remove('sold-out-hover-modal--visible');
    },

    getAlternatives(productItem) {
      const alternatives = [];

      // Prefer alternatives from the same carousel/grid as the sold-out card, then
      // fall back to the rest of the page, so suggestions stay on-topic.
      const cards = [];
      CARD_PROFILES.forEach(profile => {
        document.querySelectorAll(profile.sel).forEach(el => cards.push({ el, profile }));
      });
      const container = productItem.parentElement;
      cards.sort((a, b) => {
        const an = container && container.contains(a.el) ? 0 : 1;
        const bn = container && container.contains(b.el) ? 0 : 1;
        return an - bn;
      });

      cards.forEach(({ el: item, profile }) => {
        // Skip if sold out, and skip the card being hovered
        if (item.classList.contains(profile.soldOut)) return;
        if (item === productItem) return;

        // Get price - try multiple strategies
        let price = 'N/A';

        // Strategy 1: Look in price row for money span
        let priceRow = item.querySelector(profile.price);
        if (priceRow) {
          // .money is the theme card; .price--current is the carousel card. Either way
          // take the LAST match, which is the current price rather than the struck-out one.
          const moneySpans = priceRow.querySelectorAll('.money, .price--current');
          if (moneySpans.length > 0) {
            price = moneySpans[moneySpans.length - 1]?.textContent.trim() || 'N/A';
          }
        }

        // Strategy 2: Get all text from price row if still N/A
        if (price === 'N/A' && priceRow) {
          const allText = priceRow.textContent.trim();
          // Extract price-like strings ($ followed by numbers, or just numbers)
          const priceMatch = allText.match(/\$[\d,.]+|\$\s*[\d,.]+/);
          if (priceMatch) {
            price = priceMatch[0];
          }
        }

        // Add to alternatives (limit to 3)
        if (alternatives.length < 3) {
          // Check if product requires medical license (doctor-only)
          const requiresLicense = item.textContent.includes('Medical License Required');

          const imgEl = item.querySelector(profile.image);
          const alternative = {
            url: item.querySelector('a[href*="/products/"]')?.href || '#',
            title: item.querySelector(profile.title)?.textContent.trim() || 'Product',
            price: price,
            image: imgEl?.currentSrc || imgEl?.src || '',
            alt: imgEl?.alt || 'Product',
            requiresLicense: requiresLicense
          };
          alternatives.push(alternative);
        }
      });

      return alternatives;
    },

    renderAlternatives(alternatives) {
      const list = document.getElementById('alternatives-list');

      if (alternatives.length === 0) {
        list.innerHTML = '<p class="sold-out-hover-modal__no-results">No alternatives available</p>';
        return;
      }

      const html = alternatives.map(alt => {
        return `
        <div class="sold-out-hover-modal__item">
          <a href="${alt.url}" class="sold-out-hover-modal__item-link">
            <div class="sold-out-hover-modal__item-image">
              ${alt.image ? `<img src="${alt.image}" alt="${alt.alt}" loading="lazy">` : '<div class="sold-out-hover-modal__placeholder">No image</div>'}
            </div>
            <div class="sold-out-hover-modal__item-info">
              <p class="sold-out-hover-modal__item-title">${alt.title}</p>
            </div>
          </a>
          ${alt.requiresLicense
            ? '<div class="sold-out-hover-modal__license-required">🔒 Medical License Required</div>'
            : `<a href="${alt.url}" class="sold-out-hover-modal__item-button">
                VIEW PRODUCT
              </a>`
          }
        </div>
      `;
      }).join('');

      list.innerHTML = html;
    },

  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SoldOutHoverModal.init());
  } else {
    SoldOutHoverModal.init();
  }
})();
